import { useEffect, useMemo, useState } from 'react'
import { Hash, Volume2 } from 'lucide-react'
import { LIMITS } from '@nexus/shared'
import { cn } from '@/lib/cn'
import { api, ApiError } from '@/lib/api'
import { useAppStore } from '@/store/app'
import { useGuildChannelTree } from '@/hooks/selectors'
import { Button, Field, Input, Modal, Select } from '@/components/ui'
import { ParticipantLimitField } from '@/components/layout/ParticipantLimitField'

/** Valor do seletor para "solto, fora de qualquer categoria". */
const SEM_CATEGORIA = 'nenhuma'

export function CreateChannelModal({
  open,
  guildId,
  categoryId,
  onClose,
}: {
  open: boolean
  guildId: string
  categoryId: string | null
  onClose: () => void
}) {
  const selectChannel = useAppStore((s) => s.selectChannel)
  const applyEvent = useAppStore((s) => s.applyEvent)
  const arvore = useGuildChannelTree(guildId)

  const [name, setName] = useState('')
  const [type, setType] = useState<'text' | 'voice'>('text')
  const [userLimit, setUserLimit] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /*
    Onde o canal vai parar.

    Criar pelo menu do servidor não passa categoria nenhuma, e o resultado era
    um canal solto no topo da barra lateral, longe dos seus iguais. Em vez de
    obrigar a escolher, o padrão vai para a categoria que já reúne canais
    daquele tipo, que é quase sempre o que se queria. `null` aqui quer dizer
    "ainda não mexeram no seletor", e é o que permite a sugestão acompanhar a
    troca entre texto e voz.
  */
  const [escolhida, setEscolhida] = useState<string | null>(null)

  const sugerida = useMemo(() => {
    if (categoryId) return categoryId

    let melhor: { id: string; quantos: number } | null = null
    for (const categoria of arvore.categories) {
      const quantos = (arvore.byCategory[categoria.id] ?? []).filter((c) => c.type === type).length
      if (quantos > 0 && (!melhor || quantos > melhor.quantos)) {
        melhor = { id: categoria.id, quantos }
      }
    }

    return melhor?.id ?? SEM_CATEGORIA
  }, [arvore, categoryId, type])

  const destino = escolhida ?? sugerida

  useEffect(() => {
    if (open) {
      setName('')
      setType('text')
      setUserLimit(0)
      setEscolhida(null)
      setError(null)
    }
  }, [open])

  const handleSubmit = async () => {
    const trimmed = name.trim()
    if (trimmed.length === 0) {
      setError('Informe um nome para o canal')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const channel = await api.guilds.createChannel(guildId, {
        name: trimmed,
        type,
        categoryId: destino === SEM_CATEGORIA ? null : destino,
        userLimit: type === 'voice' ? userLimit : 0,
      })
      applyEvent({ op: 'channel_create', d: channel, s: 0 })
      selectChannel(channel.id)
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível criar o canal')
    } finally {
      setSaving(false)
    }
  }

  // O servidor normaliza o nome do mesmo jeito; mostrar a prévia evita a
  // surpresa de digitar "Bate Papo" e ver "bate-papo" aparecer na lista.
  const preview = name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\-_]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Criar canal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={() => void handleSubmit()} loading={saving}>
            Criar canal
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Tipo de canal">
          <div className="grid gap-2 sm:grid-cols-2">
            <TypeOption
              selected={type === 'text'}
              onClick={() => setType('text')}
              icon={<Hash className="size-5" />}
              title="Texto"
              description="Mensagens, arquivos e links."
            />
            <TypeOption
              selected={type === 'voice'}
              onClick={() => setType('voice')}
              icon={<Volume2 className="size-5" />}
              title="Voz"
              description="Conversa, vídeo e tela."
            />
          </div>
        </Field>

        <Field
          label="Categoria"
          hint={
            destino === SEM_CATEGORIA
              ? 'O canal fica no topo da lista, fora das categorias.'
              : undefined
          }
        >
          <Select value={destino} onChange={(e) => setEscolhida(e.target.value)}>
            {arvore.categories.map((categoria) => (
              <option key={categoria.id} value={categoria.id}>
                {categoria.name}
              </option>
            ))}
            <option value={SEM_CATEGORIA}>Sem categoria</option>
          </Select>
        </Field>

        <Field
          label="Nome"
          required
          error={error ?? undefined}
          hint={preview && preview !== name.trim().toLowerCase() ? `Ficará: ${preview}` : undefined}
        >
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void handleSubmit()}
            maxLength={LIMITS.channelName.max}
            placeholder={type === 'text' ? 'assuntos-gerais' : 'Sala de reunião'}
          />
        </Field>

        {type === 'voice' ? (
          <Field label="Quantas pessoas cabem">
            <ParticipantLimitField valor={userLimit} onChange={setUserLimit} />
          </Field>
        ) : null}
      </div>
    </Modal>
  )
}

function TypeOption({
  selected,
  onClick,
  icon,
  title,
  description,
}: {
  selected: boolean
  onClick: () => void
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-start gap-3 rounded-lg border p-3 text-left transition-colors',
        selected
          ? 'border-brand bg-brand-subtle'
          : 'border-line bg-surface-base hover:border-line-strong',
      )}
    >
      <span className={cn('mt-0.5 shrink-0', selected ? 'text-brand' : 'text-content-muted')}>
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-content-primary">{title}</span>
        <span className="block text-xs text-content-secondary">{description}</span>
      </span>
    </button>
  )
}
